import { CloseOutlined, UsergroupAddOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { DeviceGroupsIcon } from 'assets/images/svg';
import { buildGroupOptionsWithType } from 'library/helpers/integrationHelpers';
import { useManagePermittedRoles } from 'library/hooks';
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectAssetGroups, selectAssetGroupsByDeviceID } from 'reduxStore/assetGroups/selectors';
import { assetGroupActions } from 'reduxStore/assetGroups/slice';
import { Button, Col, Divider, Empty, Flex, Modal, Row, Select, Tag, Tooltip, Typography } from 'zui/Antd';

const { Title, Text } = Typography;

const ManageDeviceGroupsModal = ({ children, visible: visibleProp, setVisible: setVisibleProp, deviceID }) => {
	const dispatch = useDispatch();
	const {
		paddingLG,
		paddingXS,
		paddingSM,
		fontSizeSM,
		colorTextDescription,
		paddingXL,
		fontWeightStrong,
		colorIcon,
		paddingXXS
	} = theme.useToken().token;

	const canUpdateGroup = useManagePermittedRoles('updateGroup');
	const permissionDeniedMessage = "You don't have permission to perform this action.";

	const allGroups = useSelector(selectAssetGroups);
	const deviceGroups = useSelector((state) => selectAssetGroupsByDeviceID(state, deviceID));
	const { loadingAddGroupAsset, loadingDeleteGroupAsset } = useSelector((state) => state.AssetGroups);

	const [newAssociatedGroups, setNewAssociatedGroups] = useState([]);
	const [removedAssociatedGroups, setRemovedAssociatedGroups] = useState([]);
	const [hasChanges, setHasChanges] = useState(false);
	const [internalVisible, setInternalVisible] = useState(false);

	const visible = visibleProp ?? internalVisible;
	const setVisible = setVisibleProp ?? setInternalVisible;

	// ✅ Available groups to add (exclude already associated groups and groups marked for removal) - memoized for performance
	const availableGroups = useMemo(() => {
		const existing = deviceGroups.map((g) => g.groupID) || [];
		const filtered = existing.filter((id) => !removedAssociatedGroups.includes(id));
		const excluded = new Set([...filtered, ...newAssociatedGroups]);

		return buildGroupOptionsWithType(allGroups.filter((g) => !excluded.has(g.groupID)));
	}, [allGroups, deviceGroups, newAssociatedGroups, removedAssociatedGroups]);

	// ✅ Handle cancel and reset changes
	const handleCancel = () => {
		setNewAssociatedGroups([]);
		setRemovedAssociatedGroups([]);
		setHasChanges(false);
		setVisible(false);
	};

	// ✅ Add group
	const handleAddGroup = (groupIDToAdd) => {
		if (removedAssociatedGroups.includes(groupIDToAdd)) {
			const updatedRemovedGroups = removedAssociatedGroups.filter((id) => id !== groupIDToAdd);
			setRemovedAssociatedGroups(updatedRemovedGroups);
			setHasChanges(newAssociatedGroups.length > 0 || updatedRemovedGroups.length > 0);
		} else {
			const updatedNewGroups = [...newAssociatedGroups, groupIDToAdd];
			setNewAssociatedGroups(updatedNewGroups);
			setHasChanges(updatedNewGroups.length > 0 || removedAssociatedGroups.length > 0);
		}
	};

	// ✅ Remove group
	const handleRemoveGroup = (groupIDToRemove) => {
		if (newAssociatedGroups.includes(groupIDToRemove)) {
			const updatedNewGroups = newAssociatedGroups.filter((id) => id !== groupIDToRemove);
			setNewAssociatedGroups(updatedNewGroups);
			setHasChanges(updatedNewGroups.length > 0 || removedAssociatedGroups.length > 0);
		} else {
			const updatedRemovedGroups = [...removedAssociatedGroups, groupIDToRemove];
			setRemovedAssociatedGroups(updatedRemovedGroups);
			setHasChanges(newAssociatedGroups.length > 0 || updatedRemovedGroups.length > 0);
		}
	};

	// ✅ Submit changes
	const handleSubmit = () => {
		const groupsToAdd = newAssociatedGroups.filter((id) => !removedAssociatedGroups.includes(id));
		const groupsToRemove = removedAssociatedGroups.filter((id) => !newAssociatedGroups.includes(id));

		// Add device to new groups
		if (groupsToAdd.length > 0) {
			groupsToAdd.forEach((groupIDToAdd) => {
				dispatch(
					assetGroupActions.addGroupAsset({
						groupID: groupIDToAdd,
						assetIDs: [deviceID]
					})
				);
			});
		}

		// Remove device from groups
		if (groupsToRemove.length > 0) {
			groupsToRemove.forEach((groupIDToRemove) => {
				dispatch(
					assetGroupActions.removeGroupAsset({
						groupID: groupIDToRemove,
						assetIDs: [deviceID]
					})
				);
			});
		}

		handleCancel();
	};

	const isLoading = loadingAddGroupAsset || loadingDeleteGroupAsset;

	return (
		<>
			<Tooltip title={!canUpdateGroup ? permissionDeniedMessage : 'Manage Device Groups'} placement='top'>
				<span onClick={(e) => canUpdateGroup && setVisible(true)}>
					{children ?? (
						<Button
							type='text'
							size='small'
							icon={<UsergroupAddOutlined color='currentColor' />}
							disabled={!canUpdateGroup}
						>
							Manage Device Groups
						</Button>
					)}
				</span>
			</Tooltip>

			<Modal
				open={visible}
				onCancel={handleCancel}
				closable
				centered
				destroyOnClose
				width={763}
				footer={[
					<>
						<Divider />

						<Flex align='center' justify='flex-end' gap={paddingSM} key='footer'>
							<Button onClick={handleCancel} type='default' disabled={isLoading}>
								Cancel
							</Button>
							<Button onClick={handleSubmit} type='primary' loading={isLoading} disabled={!hasChanges}>
								Save Changes
							</Button>
						</Flex>
					</>
				]}
				styles={{ content: { padding: `${paddingLG}px ${paddingXL}px` } }}
			>
				<Row gutter={[paddingLG, paddingLG]} align='middle'>
					<Col span={24}>
						<Title level={4} style={{ margin: 0 }}>
							Manage Device Groups
						</Title>
						<Text style={{ lineHeight: '20px', fontSize: fontSizeSM, color: colorTextDescription }}>
							Add or remove this device from device groups.
						</Text>
					</Col>

					<Col span={24}>
						<Flex vertical gap={paddingXS}>
							<Text strong>Add Device Group</Text>
							<Select
								showSearch
								placeholder='Select Device Groups'
								style={{ width: '100%' }}
								options={availableGroups}
								value={null}
								onSelect={handleAddGroup}
								filterOption={(input, option) =>
									(option?.searchLabel || option?.displayLabel || '')
										.toLowerCase()
										.includes(input.toLowerCase())
								}
							/>
						</Flex>
					</Col>

					<Col span={24}>
						<Flex vertical gap={paddingSM}>
							<Text strong>Existing Device Groups</Text>
							{deviceGroups.filter((g) => !removedAssociatedGroups.includes(g.groupID)).length === 0 &&
							newAssociatedGroups.length === 0 ? (
								<Flex
									justify='center'
									align='center'
									style={{
										width: '100%',
										height: 85
									}}
								>
									<Empty
										image={Empty.PRESENTED_IMAGE_SIMPLE}
										description='No device groups yet. Add groups using the dropdown above.'
									/>
								</Flex>
							) : (
								<Flex
									wrap='wrap'
									gap={paddingSM}
									align='flex-start'
									style={{
										height: 85,
										overflowY: 'auto',
										alignContent: 'flex-start',
										alignItems: 'flex-start'
									}}
								>
									{' '}
									{deviceGroups
										.filter((group) => !removedAssociatedGroups.includes(group.groupID))
										.map((group) => {
											return (
												<Tag
													key={`existing-${group.groupID}`}
													color='blue'
													style={{
														fontWeight: fontWeightStrong,
														borderColor: 'currentColor',
														display: 'flex',
														alignItems: 'center',
														marginInlineEnd: 0
													}}
												>
													<Flex align='center' gap={paddingXXS}>
														<DeviceGroupsIcon color='currentColor' />
														{group.name}
														<CloseOutlined
															style={{ cursor: 'pointer', color: colorIcon }}
															onClick={() => handleRemoveGroup(group.groupID)}
														/>
													</Flex>
												</Tag>
											);
										})}
									{newAssociatedGroups.map((groupID) => {
										const group = allGroups.find((g) => g.groupID === groupID);
										return (
											<Tag
												key={`new-${groupID}`}
												color='blue'
												style={{
													fontWeight: fontWeightStrong,
													borderColor: 'currentColor',
													opacity: 0.7,
													display: 'flex',
													alignItems: 'center',
													marginInlineEnd: 0
												}}
											>
												<Flex align='center' gap={paddingXXS}>
													<DeviceGroupsIcon color='currentColor' />
													{group?.name} (New)
													<CloseOutlined
														style={{ cursor: 'pointer', color: colorIcon }}
														onClick={() => handleRemoveGroup(groupID)}
													/>
												</Flex>
											</Tag>
										);
									})}
								</Flex>
							)}
						</Flex>
					</Col>
				</Row>
			</Modal>
		</>
	);
};

export default ManageDeviceGroupsModal;
